import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

import { faCommentsAlt } from "@fortawesome/pro-duotone-svg-icons";
import { faXmark } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { TextEditor, toast } from "@performyard/py-components";
import type { TextEditorRef } from "@performyard/py-components";
//utils
import { sanitizeHtmlContent, validateContentSecurity } from "@performyard/utils-isomorphic";

//cmoponents
import Button from "../../../../components/Button";
import IconButton from "../../../../components/Button/IconButton";
import Chip from "../../../../components/Chip";
import { MultipleSelectEmployee } from "../../../../components/Inputs/SelectEmployee";
import type { EmployeeValue } from "../../../../components/Inputs/SelectEmployee";
import Panel from "../../../../components/Panel";

//api
import { useIsFeedbackRequestValid, useMutateSingleFeedbackRequest } from "../../../../api/hooks/defs/feedback";

//store
import { useRequestFeedbackDrawerStore } from "../../FeedbackDrawerStore";

const FEEDBACK_REQUEST_MAX_LENGTH = 560;

// Focuses the wrapped TextEditor once on mount.
// Mirrors the legacy TextEditor's AutofocusPlugin
const AutoFocusedTextEditor = ({ ref: _ignored, ...props }: React.ComponentProps<typeof TextEditor>) => {
	const ref = useRef<TextEditorRef>(null);
	useLayoutEffect(() => {
		ref.current?.focus();
	}, []);
	return <TextEditor {...props} ref={ref} />;
};

const FeedbackRequestDrawer = () => {
	const { closeDrawer, isOpen, requestData } = useRequestFeedbackDrawerStore();
	const isExistingRequest = !!requestData?.requestId;
	const [employees, setEmployees] = useState<EmployeeValue[]>([]);
	const [message, setMessage] = useState<string>("");
	const { mutate, isPending } = useMutateSingleFeedbackRequest({
		onSuccess: closeDrawer,
	});
	const { data: isValid, isLoading } = useIsFeedbackRequestValid(employees.map((v) => v.id));

	useEffect(() => {
		if (isOpen) {
			setMessage(requestData?.message ?? "");
			if (!isExistingRequest && requestData?.requestedTo) {
				setEmployees([{ id: requestData.requestedTo._id, name: requestData.requestedTo.name }]);
			} else {
				setEmployees([]);
			}
		}
	}, [isOpen]);

	const handleSubmit = () => {
		const sanitizedMessage = sanitizeHtmlContent(message);

		const validation = validateContentSecurity(sanitizedMessage);
		if (!validation.isValid) {
			toast.error("Invalid feedback content");
			return;
		}

		if (isExistingRequest) {
			mutate({ id: requestData.requestId, message: sanitizedMessage });
		} else {
			mutate({ message: sanitizedMessage, requestedTo: employees.map((v) => v.id) });
		}
	};

	return (
		<Panel side="right" isOpen={isOpen} onClose={closeDrawer} width={480}>
			<div className="p-6">
				<h2
					data-testid="request-feedback-title"
					className="margin-auto max-w-full text-center text-[30px] font-[900] text-primary-5"
				>
					<FontAwesomeIcon icon={faCommentsAlt} /> REQUEST FEEDBACK
				</h2>

				{isExistingRequest && requestData?.requestedTo ? (
					<div className="mt-5">
						<span className="pb-1 text-paragraph-3 font-semi-bold">Requesting Feedback From:</span>
						<Chip avatarID={requestData.requestedTo._id} label={requestData.requestedTo.name} />
					</div>
				) : (
					<div id="user input" data-testid="user-input" className="mt-5">
						<span className="text-paragraph-3 font-semi-bold">
							Who would you like to request feedback from?
						</span>
						<MultipleSelectEmployee
							placeholder="Select an employee..."
							onChange={setEmployees}
							value={employees}
							excludeCurrentUser
							ariaLabel="Who would you like to request feedback from?"
						/>
					</div>
				)}
				{isLoading || employees.length === 0 || isValid?.is_allowed ? null : (
					<span className="mt-2 text-caption text-red-5">
						One or more of the selected employees have already been requested in the past 14 days.
					</span>
				)}
				<div id="message" className="mt-4 mb-6">
					<span className="text-paragraph-3 font-semi-bold">Optional Message</span>
					<AutoFocusedTextEditor
						id="request-feedback-message"
						initialValue={message}
						onChange={setMessage}
						characterLimit={FEEDBACK_REQUEST_MAX_LENGTH}
						minHeight={200}
					/>
				</div>
				{isExistingRequest || employees.length > 0 ? (
					<div className="fixed bottom-0 w-[465px] max-w-full bg-neutral-0">
						<Button
							isRequesting={isPending}
							disabled={
								isPending ||
								message.length > FEEDBACK_REQUEST_MAX_LENGTH ||
								(!isExistingRequest && !isValid?.is_allowed)
							}
							size="large"
							style={{ width: "calc(100% - 24px)", margin: 8 }}
							onClick={handleSubmit}
						>
							{isExistingRequest ? "Edit" : "Create"} Request
						</Button>
					</div>
				) : null}

				<IconButton
					testId="request-feedback-close-button"
					icon={faXmark}
					className="absolute top-2 right-2"
					onClick={closeDrawer}
					aria-label="Close feedback request drawer"
				/>
			</div>
		</Panel>
	);
};

export default FeedbackRequestDrawer;
